import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { IconBook, IconCampaignBriefing } from '../../../../../shared/Icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { formatDate, hasAccessRights, truncateString } from '../../../../../shared/functions/Functions';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import Tooltip from '@mui/material/Tooltip';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { useNavigate } from 'react-router-dom';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import { MUTED_TEXT_COLOR } from '../../../../../utils/constants';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { BriefingDashCardSkeleton } from './CampaignDashboardSkeletons';

export function CampaignBriefingDashCard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { campaign, isChildCampaign } = useSelector((state: IAllStates) => state.selectedCampaign);
    if (!campaign) {
        return <BriefingDashCardSkeleton />;
    }
    return (
        <CardLayout classCard="mb-3 mb-md-0">
            <div className="mb-3">
                <div className="d-md-flex align-items-center justify-content-between">
                    <h6
                        onClick={() => navigate('/brand/create/campaign/' + campaign?.id + '/overview')}
                        className="textHover d-flex mb-0 cursor-pointer"
                    >
                        <IconCampaignBriefing
                            width="20"
                            height="20"
                            color={MUTED_TEXT_COLOR}
                            classes={'mr-2 textHover'}
                        />
                        {t('brand.campaign.dashboard.campaign_briefing')}
                    </h6>
                    {isChildCampaign && (
                        <p className="mb-0 text-muted mr-1 mt-1 mt-md-0">
                            {t('brand.campaign.dashboard.booking.part_of_long_term')}
                            <span
                                onClick={() => navigate(`/brand/campaign/dashboard/${campaign?.parent?.id}`)}
                                className="mb-0 cursor-pointer textHoverStrong font-weight-bold ml-1"
                            >
                                {truncateString(campaign?.parent?.title ?? '', 50)}
                            </span>
                        </p>
                    )}
                </div>
            </div>
            <Row className="align-items-end">
                <Col lg={2} className="col-6 px-0 mb-2 mb-lg-0">
                    <h6 className="text-primary text-center">
                        {campaign?.isCharity
                            ? t('brand.campaign.dashboard.barter_campaign')
                            : t('brand.campaign.dashboard.paid_campaign')}
                    </h6>
                    <p className="mb-0 text-center">{t('brand.campaign.dashboard.type')}</p>
                </Col>
                <Col lg={2} className="col-6 px-0 mb-2 mb-lg-0">
                    <h6 className="text-primary text-center">{campaign?.campaignGoal?.title ?? '-'}</h6>
                    <p className="mb-0 text-center">{t('brand.campaign.dashboard.campaign_goal')}</p>
                </Col>
                <Col lg={!campaign?.isCharity ? 2 : 4} className="col-6 px-0 mb-2 mb-lg-0">
                    <h6 className="d-flex justify-content-center">
                        {campaign?.mainPlatforms?.length ? (
                            campaign?.mainPlatforms?.slice(0, 3)?.map(platform => (
                                <div className="mx-1" key={platform?.id}>
                                    <CustomImageHandler
                                        photoPath={platform?.path as string}
                                        altTag={platform?.name ?? 'Social platform image'}
                                    />
                                </div>
                            ))
                        ) : (
                            <span className="text-primary">-</span>
                        )}
                        {(campaign?.mainPlatforms?.length ?? 0) > 3 && (
                            <Tooltip
                                id="white-tooltip"
                                title={
                                    <>
                                        {campaign?.mainPlatforms?.slice(3)?.map(platform => (
                                            <div className="mx-1 my-2" key={platform?.id}>
                                                <CustomImageHandler
                                                    style={{ height: 33 }}
                                                    photoPath={platform?.path as string}
                                                    altTag={platform?.name ?? 'Social platform image'}
                                                />
                                            </div>
                                        ))}
                                    </>
                                }
                                placement="right"
                                className="cursor-pointer"
                            >
                                <span
                                    className="icon-round bg-dark font-weight-semi-bold min-width-max-content"
                                    style={{ width: 33 }}
                                >
                                    + {(campaign?.mainPlatforms?.length ?? 0) - 3}
                                </span>
                            </Tooltip>
                        )}
                    </h6>
                    <p className="mb-0 text-center">{t('general.statistics.platforms')}</p>
                </Col>
                <Col lg={2} className="col-6 px-0 mb-2 mb-lg-0">
                    <h6 className="text-primary text-center">
                        {campaign?.date ? formatDate(campaign?.date as string) : 'DD.MM.YYYY.'}
                    </h6>
                    <p className="mb-0 text-center">{t('brand.campaign.dashboard.app_deadline')}</p>
                </Col>
                <Col lg={2} className="col-6 px-0 mb-2 mb-lg-0">
                    <h6 className="text-primary text-center w-maxContent w-auto">
                        {campaign?.publishFrom ? formatDate(campaign?.publishFrom as string) : 'DD.MM.YYYY.'} -
                        {campaign?.publishTo ? formatDate(campaign?.publishTo as string) : ' DD.MM.YYYY.'}
                    </h6>
                    <p className="mb-0 text-center">{t('brand.campaign.dashboard.pub_period')}</p>
                </Col>
                {!campaign?.isCharity && hasAccessRights(AccessRightEnum.CAN_EXPORT_MEASURE) && (
                    <Col lg={2} className="col-6 px-0 mb-2 mb-lg-0">
                        <h6 className="text-primary text-center euro-symbol">
                            {priceSplitter(campaign?.grossBudget ?? 0)}
                        </h6>
                        <p className="mb-0 text-center">{t('brand.campaign.dashboard.campaign_budget')}</p>
                    </Col>
                )}
            </Row>
        </CardLayout>
    );
}
